/**
 * 🆕 ConfirmModal — modal padronizado de confirmação
 *
 * Substitui o anti-padrão `window.confirm()` que estava espalhado em 200+
 * call-sites do frontend. Visual consistente com o resto do design system
 * (Modal padrão da plataforma).
 *
 * 3 variantes pré-definidas:
 *   - 'danger'  → vermelho, ação destrutiva (deletar, arquivar, remover)
 *   - 'warning' → laranja, ação reversível mas com efeito (pausar, etc.)
 *   - 'info'    → azul (default), confirmação neutra
 *
 * Uso imperativo: `const { confirm } = useConfirm()`
 *     const ok = await confirm({ title, message, variant: 'danger' });
 *     if (!ok) return;
 *     // ... ação ...
 *
 * Uso declarativo (controle direto do `show`):
 *   <ConfirmModal
 *     show={open}
 *     onHide={() => setOpen(false)}
 *     onConfirm={handleAction}
 *     title="Remover conta?"
 *     message="Essa ação não pode ser desfeita."
 *     variant="danger"
 *     confirmLabel="Remover"
 *   />
 */

import React from 'react';
import { Icon } from '@iconify/react';
import Modal from './Modal';

const VARIANTS = {
  danger: {
    icon: 'mdi:alert-octagon',
    color: '#dc2626',
    background: '#fee2e2',
    confirmVariant: 'danger',
    defaultConfirmLabel: 'Remover',
  },
  warning: {
    icon: 'mdi:alert',
    color: '#d97706',
    background: '#fef3c7',
    confirmVariant: 'warning',
    defaultConfirmLabel: 'Continuar',
  },
  info: {
    icon: 'mdi:information',
    color: '#2563eb',
    background: '#dbeafe',
    confirmVariant: 'primary',
    defaultConfirmLabel: 'Confirmar',
  },
  success: {
    icon: 'mdi:check-circle',
    color: '#059669',
    background: '#d1fae5',
    confirmVariant: 'success',
    defaultConfirmLabel: 'OK',
  },
};

const ConfirmModal = ({
  show,
  onHide,
  onConfirm,
  title = 'Confirmar?',
  message,
  details = null,
  variant = 'info',
  confirmLabel,
  cancelLabel = 'Cancelar',
  loading = false,
  size = 'md',
}) => {
  const v = VARIANTS[variant] || VARIANTS.info;
  const finalConfirmLabel = confirmLabel || v.defaultConfirmLabel;

  const handleConfirm = async () => {
    if (loading) return;
    await onConfirm?.();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={title}
      icon={v.icon}
      size={size}
      footer={
        <>
          <Modal.Button variant="secondary" onClick={onHide} disabled={loading}>
            {cancelLabel}
          </Modal.Button>
          <Modal.Button variant={v.confirmVariant} onClick={handleConfirm} disabled={loading} icon={loading ? 'mdi:loading' : v.icon}>
            {finalConfirmLabel}
          </Modal.Button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: 8 }}>
        <div
          style={{
            flexShrink: 0,
            width: 48,
            height: 48,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: v.background,
            color: v.color,
          }}
        >
          <Icon icon={v.icon} width="28" />
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 15, color: '#374151', lineHeight: 1.5 }}>
            {message}
          </span>
          {details && (
            <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#6b7280' }}>
              {details}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
export { VARIANTS };
